import { useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { ChevronDown, ChevronRight, ClipboardCopy, Scale } from "lucide-react"

import type { FillRateFineReview, FineReviewLine } from "@/api/inbound_po_line.tsx"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { format_rate } from "@/fill_rate/fill_rate_display.ts"
import { exactCurrencyFormatter } from "@/utils"

interface FillRateFineReviewPanelProps {
  review: FillRateFineReview
  className?: string
}

function LineRow({ line }: { line: FineReviewLine }) {
  return (
    <tr class="border-b last:border-0 align-top">
      <td class="py-1 pr-3 whitespace-nowrap">{line.received_date ? format(parseISO(line.received_date), "MMM d") : "—"}</td>
      <td class="py-1 pr-3 font-mono">{line.po_number}</td>
      <td class="py-1 pr-3 font-mono">{line.item_number}</td>
      <td class="py-1 pr-3 text-right">
        {line.received} / {line.ordered}
        {line.alert_received !== null && line.alert_received !== line.received && (
          <div class="text-amber-700">UNFI alert: {line.alert_received}</div>
        )}
      </td>
      <td class="py-1 text-right">{exactCurrencyFormatter(line.fine)}</td>
    </tr>
  )
}

/**
 * A UNFI Canada `LOBLAWS FILL RATE` chargeback set against Loblaw's fine policy and UNFI's own
 * receiving record, with the claim the findings support. Shown only for those chargebacks.
 */
export function FillRateFineReviewPanel({ review, className }: FillRateFineReviewPanelProps) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight

  async function copy_claim() {
    await navigator.clipboard.writeText(review.claim_message)
    toast({ title: "Claim copied", description: "Paste it into the dispute to CanadaSupplierClaims@unfi.com." })
  }

  return (
    <div class={`border border-slate-200 rounded-lg overflow-hidden ${className ?? ""}`}>
      <button type="button" class="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-50 hover:bg-slate-100" onClick={() => setOpen(!open)}>
        <Chevron className="h-4 w-4 text-slate-500" />
        <Scale className="h-4 w-4 text-slate-600" />
        <span class="text-sm font-medium">Fill-rate fine review</span>
        <span class="ml-auto text-xs text-slate-500">
          {review.findings.length} finding{review.findings.length === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <div class="p-3 flex flex-col gap-3 text-sm">
          <div class="grid grid-cols-2 gap-2">
            <div>
              <div class="text-xs text-slate-500">Charged</div>
              <div class="font-medium">
                {exactCurrencyFormatter(review.fine_total)} ({format_rate(review.charged_rate)} of shortfall)
              </div>
            </div>
            <div>
              <div class="text-xs text-slate-500">Loblaw policy (10%)</div>
              <div class="font-medium">{exactCurrencyFormatter(review.amount_at_policy_rate)}</div>
              <div class="text-xs text-slate-500">plus $1,000 minimum per Loblaw short line</div>
            </div>
            <div>
              <div class="text-xs text-slate-500">Shorted value</div>
              <div class="font-medium">{exactCurrencyFormatter(review.shorted_value_total)}</div>
            </div>
            <div>
              <div class="text-xs text-slate-500">Your rolling fill rate</div>
              <div class="font-medium">{format_rate(review.rolling_fill_rate)}</div>
            </div>
          </div>

          {review.findings.length > 0 ? (
            <ul class="list-disc pl-5 space-y-1 text-slate-700">
              {review.findings.map((finding: string, i: number) => (
                <li key={i}>{finding}</li>
              ))}
            </ul>
          ) : (
            <div class="text-slate-500">Every fined line matches UNFI's receiving record and the policy rate; nothing to dispute on the facts.</div>
          )}

          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-slate-500 border-b">
                  <th class="py-1 pr-3 font-medium">Received</th>
                  <th class="py-1 pr-3 font-medium">PO</th>
                  <th class="py-1 pr-3 font-medium">Item</th>
                  <th class="py-1 pr-3 font-medium text-right">Rec / ord</th>
                  <th class="py-1 font-medium text-right">Fine</th>
                </tr>
              </thead>
              <tbody>
                {review.lines.map((line: FineReviewLine) => (
                  <LineRow key={`${line.po_number}-${line.item_number}`} line={line} />
                ))}
              </tbody>
            </table>
          </div>

          {review.findings.length > 0 && (
            <Button size="sm" variant="outline" onClick={copy_claim}>
              <ClipboardCopy className="h-4 w-4" /> Copy claim for UNFI Canada
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
